/**
 * Agent Performance — the Analytics view.
 *
 * Reads the SAME rows the table renders (the report is fetched whole, bounded
 * by the filter drawer), so switching tabs never re-queries and the two views
 * can never disagree.
 *
 * The report returns one row per agent — there are no period buckets — so
 * there is deliberately no trend line here; a fabricated x-axis would be worse
 * than none. Every panel answers a cross-agent question instead: who carried
 * the volume, who is closing it, whether attainment holds as load rises, and
 * which agents need looking at.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  Activity, AlertTriangle, Gauge, Layers, ListOrdered, Timer, TrendingUp, Users,
} from 'lucide-react'

import type { AgentPerformanceRow } from '../../../hooks/useAgentPerformanceReport.js'
import {
  AnalyticsCanvas,
  AttentionList,
  CardEmpty,
  CardGrid,
  ChartCard,
  HeroBand,
  KpiRow,
  Meter,
  RankTable,
  SectionLabel,
  StatTile,
  type AttentionItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  formatCount,
  formatPct,
  formatSeconds,
  ratePct,
  scatterDataset,
  scatterOptions,
  sumBy,
  weightedMean,
} from '../analytics/viz.js'

/** One agent's totals — rows are already per-agent, but duplicates are merged defensively. */
interface AgentAgg {
  key: string
  name: string
  assigned: number
  closed: number
  workInProgress: number
  reOpened: number
  transferByAgent: number
  notes: number
  responses: number
  avgResponseSeconds: number | null
  avgResolutionSeconds: number | null
  responseSlaPct: number | null
  resolutionSlaPct: number | null
}

/** How many agents a per-agent chart shows before the tail is dropped. */
const CHART_AGENTS = 8
/** Below this, an agent is called out in the attention panel. */
const SLA_TARGET = 90
/** Re-open share that reads as a quality problem rather than noise. */
const REOPEN_TARGET = 10

function aggregate(rows: readonly AgentPerformanceRow[]): AgentAgg[] {
  const byAgent = new Map<string, AgentPerformanceRow[]>()
  for (const row of rows) {
    const key = row.agentUserId || row.agentName
    const bucket = byAgent.get(key)
    if (bucket) bucket.push(row)
    else byAgent.set(key, [row])
  }

  const out: AgentAgg[] = []
  for (const [key, group] of byAgent) {
    const first = group[0]
    if (!first) continue
    out.push({
      key,
      name: first.agentName,
      assigned: sumBy(group, (r) => r.assigned),
      closed: sumBy(group, (r) => r.closed),
      workInProgress: sumBy(group, (r) => r.workInProgress),
      reOpened: sumBy(group, (r) => r.reOpened),
      transferByAgent: sumBy(group, (r) => r.transferByAgent),
      notes: sumBy(group, (r) => r.notes),
      responses: sumBy(group, (r) => r.responses),
      // Averages are weighted by the volume each one summarises, never by row count.
      avgResponseSeconds: weightedMean(group.map((r) => [r.avgResponseSeconds, r.responses])),
      avgResolutionSeconds: weightedMean(group.map((r) => [r.avgResolutionSeconds, r.closed])),
      responseSlaPct: weightedMean(group.map((r) => [r.responseSlaPct, r.assigned])),
      resolutionSlaPct: weightedMean(group.map((r) => [r.resolutionSlaPct, r.closed])),
    })
  }
  return out
}

export default function AgentPerformanceAnalytics({
  rows,
}: {
  rows: readonly AgentPerformanceRow[]
}): JSX.Element {
  const agents = useMemo(() => aggregate(rows), [rows])

  const totals = useMemo(() => {
    const assigned = sumBy(agents, (a) => a.assigned)
    const closed = sumBy(agents, (a) => a.closed)
    return {
      assigned,
      closed,
      workInProgress: sumBy(agents, (a) => a.workInProgress),
      reOpened: sumBy(agents, (a) => a.reOpened),
      responses: sumBy(agents, (a) => a.responses),
      notes: sumBy(agents, (a) => a.notes),
      transfers: sumBy(agents, (a) => a.transferByAgent),
      closureRate: ratePct(closed, assigned),
      // Org-wide averages weight each agent by the volume behind their number.
      avgResponse: weightedMean(agents.map((a) => [a.avgResponseSeconds, a.responses])),
      avgResolution: weightedMean(agents.map((a) => [a.avgResolutionSeconds, a.closed])),
      responseSla: weightedMean(agents.map((a) => [a.responseSlaPct, a.assigned])),
      resolutionSla: weightedMean(agents.map((a) => [a.resolutionSlaPct, a.closed])),
      activeAgents: agents.filter((a) => a.assigned > 0 || a.responses > 0).length,
    }
  }, [agents])

  const byVolume = useMemo(
    () => [...agents].sort((a, b) => b.assigned - a.assigned || b.closed - a.closed),
    [agents],
  )
  const maxAssigned = byVolume[0]?.assigned ?? 0

  const topForCharts = useMemo(
    () => byVolume.filter((a) => a.assigned > 0 || a.closed > 0).slice(0, CHART_AGENTS),
    [byVolume],
  )

  /** Volume spread across the roster — the sparkline behind the hero figure. */
  const volumeShape = useMemo(() => byVolume.map((a) => a.assigned).slice(0, 24), [byVolume])

  // ── Assigned vs closed ─────────────────────────────────────────────────────
  const handledData = useMemo(
    () => ({
      labels: topForCharts.map((a) => a.name),
      datasets: [
        barDataset('Assigned', topForCharts.map((a) => a.assigned), VIZ.series[0]),
        barDataset('Closed', topForCharts.map((a) => a.closed), VIZ.series[2]),
      ],
    }),
    [topForCharts],
  )

  // ── SLA attainment ─────────────────────────────────────────────────────────
  const slaAgents = useMemo(
    () =>
      byVolume
        .filter((a) => a.responseSlaPct !== null || a.resolutionSlaPct !== null)
        .slice(0, CHART_AGENTS),
    [byVolume],
  )
  const slaData = useMemo(
    () => ({
      labels: slaAgents.map((a) => a.name),
      datasets: [
        barDataset('Response SLA', slaAgents.map((a) => a.responseSlaPct ?? 0), VIZ.series[0]),
        barDataset('Resolution SLA', slaAgents.map((a) => a.resolutionSlaPct ?? 0), VIZ.series[1]),
      ],
    }),
    [slaAgents],
  )

  // ── Handling time ──────────────────────────────────────────────────────────
  const timeAgents = useMemo(
    () =>
      byVolume
        .filter((a) => a.avgResponseSeconds !== null || a.avgResolutionSeconds !== null)
        .slice(0, CHART_AGENTS),
    [byVolume],
  )
  const timeData = useMemo(
    () => ({
      labels: timeAgents.map((a) => a.name),
      datasets: [
        barDataset('First response', timeAgents.map((a) => a.avgResponseSeconds ?? 0), VIZ.series[0]),
        barDataset('Resolution', timeAgents.map((a) => a.avgResolutionSeconds ?? 0), VIZ.series[1]),
      ],
    }),
    [timeAgents],
  )

  // ── Workload mix ───────────────────────────────────────────────────────────
  const mixData = useMemo(
    () => ({
      labels: topForCharts.map((a) => a.name),
      datasets: [
        barDataset('Closed', topForCharts.map((a) => a.closed), VIZ.series[2], { stacked: true }),
        barDataset('In progress', topForCharts.map((a) => a.workInProgress), VIZ.series[0], { stacked: true }),
        barDataset('Re-opened', topForCharts.map((a) => a.reOpened), VIZ.series[1], { stacked: true }),
      ],
    }),
    [topForCharts],
  )

  // ── Load vs attainment ─────────────────────────────────────────────────────
  //
  // The question a table cannot answer at a glance: does service level hold as
  // an agent's load goes up? A falling cloud left-to-right is the finding.
  const scatterAgents = useMemo(
    () => agents.filter((a) => a.assigned > 0 && a.responseSlaPct !== null),
    [agents],
  )
  const scatterData = useMemo(
    () => ({
      datasets: [
        scatterDataset(
          'Agents',
          scatterAgents.map((a) => ({ x: a.assigned, y: a.responseSlaPct ?? 0 })),
          VIZ.series[0],
        ),
      ],
    }),
    [scatterAgents],
  )

  // ── Needs attention ────────────────────────────────────────────────────────
  const attention = useMemo((): AttentionItem[] => {
    const items: AttentionItem[] = []
    for (const agent of agents) {
      if (agent.assigned === 0 && agent.responses === 0) continue
      const response = agent.responseSlaPct
      const resolution = agent.resolutionSlaPct
      const reopenRate = ratePct(agent.reOpened, agent.assigned)

      if (response !== null && response < SLA_TARGET) {
        items.push({
          id: `${agent.key}-response`,
          title: agent.name,
          detail: `Response SLA over ${formatCount(agent.assigned)} assigned`,
          value: formatPct(response, 1),
          tone: response < 75 ? 'critical' : 'warning',
        })
      } else if (resolution !== null && resolution < SLA_TARGET) {
        items.push({
          id: `${agent.key}-resolution`,
          title: agent.name,
          detail: `Resolution SLA over ${formatCount(agent.closed)} closed`,
          value: formatPct(resolution, 1),
          tone: resolution < 75 ? 'critical' : 'warning',
        })
      } else if (reopenRate !== null && reopenRate > REOPEN_TARGET) {
        items.push({
          id: `${agent.key}-reopen`,
          title: agent.name,
          detail: `${formatCount(agent.reOpened)} re-opened of ${formatCount(agent.assigned)}`,
          value: formatPct(reopenRate, 1),
          tone: 'warning',
        })
      }
    }
    // Worst first — the panel is a work queue, not a directory.
    return items.sort((a, b) => parseFloat(a.value) - parseFloat(b.value)).slice(0, 8)
  }, [agents])

  if (agents.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no agent activity. Widen the window or clear the team and
            agent filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const percentAxis = barOptions({
    horizontal: true,
    legend: true,
    max: 100,
    format: (v) => `${Math.round(v)}%`,
  })
  const topAgent = byVolume[0]

  return (
    <AnalyticsCanvas>
      {/* ── The one number the view leads with ── */}
      <HeroBand
        eyebrow="Conversations assigned"
        value={formatCount(totals.assigned)}
        caption={`Handled by ${totals.activeAgents} agent${totals.activeAgents === 1 ? '' : 's'} in the selected window`}
        spark={volumeShape}
        Icon={Users}
        facts={[
          { label: 'Closed', value: formatCount(totals.closed) },
          { label: 'Closure rate', value: formatPct(totals.closureRate, 1) },
          {
            label: 'Response SLA',
            value: formatPct(totals.responseSla, 1),
            tone: slaTone(totals.responseSla),
          },
          {
            label: 'Resolution SLA',
            value: formatPct(totals.resolutionSla, 1),
            tone: slaTone(totals.resolutionSla),
          },
        ]}
      />

      {/* ── Supporting numbers ── */}
      <KpiRow>
        <StatTile
          label="In progress"
          value={formatCount(totals.workInProgress)}
          hint="Assigned but not yet closed"
        />
        <StatTile
          label="Re-opened"
          value={formatCount(totals.reOpened)}
          hint={`${formatPct(ratePct(totals.reOpened, totals.assigned), 1)} of assigned`}
          tone={reopenTone(ratePct(totals.reOpened, totals.assigned))}
        />
        <StatTile
          label="Avg first response"
          value={formatSeconds(totals.avgResponse)}
          hint={`${formatCount(totals.responses)} replies sent`}
        />
        <StatTile label="Avg resolution" value={formatSeconds(totals.avgResolution)} />
        <StatTile
          label="Notes added"
          value={formatCount(totals.notes)}
          hint={`${formatCount(totals.transfers)} transfers out`}
        />
        <StatTile
          label="Agents reporting"
          value={formatCount(totals.activeAgents)}
          {...(topAgent ? { hint: `Busiest: ${topAgent.name}` } : {})}
        />
      </KpiRow>

      {/* ── Leaderboard ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${agents.length} agent${agents.length === 1 ? '' : 's'} in scope`}>
          Leaderboard
        </SectionLabel>
        <ChartCard
          title="Every agent, ranked by volume"
          subtitle="Bar length shows each agent's share of the busiest agent's load"
          Icon={ListOrdered}
          badge="Scroll for more"
          height={340}
          wide
        >
          <RankTable
            rows={byVolume}
            keyOf={(a) => a.key}
            nameOf={(a) => a.name}
            shareOf={(a) => (maxAssigned > 0 ? a.assigned / maxAssigned : 0)}
            columns={[
              { label: 'Assigned', align: 'right', render: (a) => formatCount(a.assigned) },
              { label: 'Closed', align: 'right', render: (a) => formatCount(a.closed) },
              {
                label: 'Closure',
                align: 'right',
                render: (a) => formatPct(ratePct(a.closed, a.assigned), 1),
              },
              { label: 'Responses', align: 'right', render: (a) => formatCount(a.responses) },
              {
                label: 'Avg response',
                align: 'right',
                render: (a) => formatSeconds(a.avgResponseSeconds),
              },
              {
                label: 'Avg resolution',
                align: 'right',
                render: (a) => formatSeconds(a.avgResolutionSeconds),
              },
              {
                label: 'Response SLA',
                align: 'right',
                render: (a) => <SlaCell value={a.responseSlaPct} />,
              },
              {
                label: 'Resolution SLA',
                align: 'right',
                render: (a) => <SlaCell value={a.resolutionSlaPct} />,
              },
            ]}
          />
        </ChartCard>
      </section>

      {/* ── Volume ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Volume &amp; throughput</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Assigned vs closed"
            subtitle={`Top ${Math.min(CHART_AGENTS, topForCharts.length)} agents by assigned volume`}
            Icon={TrendingUp}
            footnote={
              totals.closureRate !== null
                ? `The team closed ${formatPct(totals.closureRate, 1)} of what it was assigned in this window.`
                : undefined
            }
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations were assigned in this window." />
            ) : (
              <Chart
                type="bar"
                data={handledData}
                options={barOptions({ horizontal: true, legend: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Workload mix"
            subtitle="Closed, still in progress and re-opened, per agent"
            Icon={Layers}
            footnote={`${formatCount(totals.workInProgress)} conversations are still open across the team.`}
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations were assigned in this window." />
            ) : (
              <Chart
                type="bar"
                data={mixData}
                options={barOptions({ horizontal: true, legend: true, stacked: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Service levels ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`Target ${SLA_TARGET}%`}>Service levels</SectionLabel>
        <CardGrid>
          <ChartCard
            title="SLA attainment by agent"
            subtitle="Share of conversations inside target"
            Icon={Gauge}
          >
            {slaAgents.length === 0 ? (
              <CardEmpty message="No SLA-measurable conversations in this window." />
            ) : (
              <Chart type="bar" data={slaData} options={percentAxis} className="h-full w-full" />
            )}
          </ChartCard>

          <ChartCard
            title="Needs attention"
            subtitle={`Agents under the ${SLA_TARGET}% target, or re-opening more than ${REOPEN_TARGET}%`}
            Icon={AlertTriangle}
          >
            <AttentionList
              items={attention}
              emptyMessage={`Every reporting agent is at or above the ${SLA_TARGET}% target.`}
            />
          </ChartCard>

          <ChartCard
            title="Load vs attainment"
            subtitle="Does service level hold as volume rises?"
            Icon={Activity}
            footnote="Each dot is one agent. A cloud sloping down to the right means attainment is falling as load grows."
          >
            {scatterAgents.length < 2 ? (
              <CardEmpty message="At least two agents with SLA-measurable volume are needed to compare." />
            ) : (
              <Chart
                type="scatter"
                data={scatterData}
                options={scatterOptions({
                  xLabel: 'Assigned',
                  yLabel: 'Response SLA',
                  yFormat: (v) => `${Math.round(v)}%`,
                  yMax: 100,
                  pointLabel: (index) => scatterAgents[index]?.name ?? 'Agent',
                })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Handling time by agent"
            subtitle="Average first response and resolution"
            Icon={Timer}
          >
            {timeAgents.length === 0 ? (
              <CardEmpty message="Nothing was responded to or resolved in this window." />
            ) : (
              <Chart
                type="bar"
                data={timeData}
                options={barOptions({ horizontal: true, legend: true, format: formatSeconds })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Outcome mix ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Outcomes</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Where conversations stand"
            subtitle="Every assigned conversation, by outcome"
            height={220}
          >
            <Meter
              segments={[
                { label: 'Closed', value: totals.closed, color: VIZ.series[2] },
                { label: 'In progress', value: totals.workInProgress, color: VIZ.series[0] },
                { label: 'Re-opened', value: totals.reOpened, color: VIZ.series[1] },
              ]}
              emptyMessage="No conversations were assigned in this window."
            />
          </ChartCard>
          <ChartCard
            title="Collaboration"
            subtitle="Notes and transfers recorded by agents"
            height={220}
          >
            <Meter
              segments={[
                { label: 'Notes added', value: totals.notes, color: VIZ.series[0] },
                { label: 'Transferred out', value: totals.transfers, color: VIZ.series[1] },
              ]}
              emptyMessage="No notes or transfers in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}

/** SLA cell: the number leads, a dot carries the state so it isn't color-only. */
function SlaCell({ value }: { value: number | null }): JSX.Element {
  const tone = slaTone(value)
  const dot =
    tone === 'good' ? 'bg-[#0CA30C]' : tone === 'warning' ? 'bg-[#FAB219]' : tone === 'critical' ? 'bg-[#D03B3B]' : 'bg-slate-300'
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {formatPct(value, 1)}
    </span>
  )
}

/**
 * SLA tone. Thresholds are the conventional 95/85 service-level bands, and the
 * color never travels alone — the label and value always say what it is.
 */
function slaTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct >= 95) return 'good'
  if (pct >= 85) return 'warning'
  return 'critical'
}

function reopenTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 5) return 'good'
  if (pct <= REOPEN_TARGET) return 'warning'
  return 'critical'
}
